import { couplingLayerExceededNotification } from 'cadenza/map/custom/workbook/workbook-map-view-notification';
import type {
  WorkbookState
} from 'cadenza/workbook/workbook.state';
import {
  synchronizeToLeftTopMostView,
  getAttributeById,
  getDataViewById,
  getNotificationsForLayer,
  getNotificationsForView
} from 'cadenza/workbook/workbook.state';
import {
  getMapCouplingGroupWithStateById,
  getMapViewSrs,
  isCouplingLayerLimitExceededForGroupByViewId
} from 'cadenza/workbook/state/workbook-map-view.state';
import type { DataViewDto } from 'cadenza/workbook/api/data-view-dto';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id';
import type { ViewDto } from 'cadenza/workbook/api/view-dto';
import type { AttributeBundleName } from 'cadenza/workbook/model/attribute-bundle-name';
import type { LayerId } from 'cadenza/workbook/api/layer-id';
import { toDataViewId } from 'cadenza/workbook/model/data-view-id-utils';
import { CHART_BUNDLE_NAMES, MAP_BUNDLE_NAMES } from 'cadenza/workbook/model/attribute-bundle-name';
import type {
  DataViewNotification,
  DataViewNotificationType,
  WorkbookNotification,
  WorkbookNotificationId
} from 'cadenza/workbook/notifications/workbook-notifications';
import { createNotification } from 'cadenza/workbook/notifications/workbook-notifications';
import { DataViewVisualizationKey } from 'cadenza/workbook/api/data-view-visualization-key';
import { isValidColorPaletteId, isValidGradientId } from 'cadenza/workbook/state/color-config.state';
import type { ActiveAttributeId } from 'cadenza/workbook/api/active-attribute-id';
import type { WorkbookAttributeDto } from 'cadenza/workbook/api/workbook-attribute-dto';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import { store } from 'cadenza/store/cadenza-store';
import { isDataViewReferenceByViewDto } from 'cadenza/workbook/map/workbook-layer-utils';
import { collectClassificationAttributeErrors } from 'cadenza/workbook/view-validation/color-classification-validation';
import { AttributeStatus, attributeStatusMessage } from 'cadenza/repository/model/attribute-status';

/**
 * This function is called from the updateWorkbookState function in 'workbook.state', in the context of reducers
 * executed on initial workbook load and when updating the state after executing a workbook command.
 * So the state that is passed in is the state being updated and thus is mutable and can be modified.
 *
 * In this function, the  views state, in particular the data views and layers, are checked for known
 * possible invalid state, or error statuses populated by the backend, and notifications are added to the state for each problem found.
 *
 * UI components that need to display the notifications (e.g. layer in the designer's layer tree) can
 * subscribe to the notifications state.
 *
 * @param state The workbook state being updated (mutable)
 */
export function validateViews (state: WorkbookState) {
  const views = state.viewById;
  Object.values(views)
    .forEach((view) => {
      const newNotifications = [
        ...validateDataViews(view, state.dataViewById, state.activeAttributeById, state)
      ];
      validateMapCouplingGroupLayerCount(newNotifications, state, view);
      addViewNotifications(newNotifications, state, view);
    });
}

export function validateMapViewLayers (state: WorkbookState) {
  const views = state.viewById;
  Object.values(views)
    .filter(view => view.type === 'MAP')
    .forEach((view) => {
      const newNotifications = [
        ...validateLayers(state, view)
      ];
      addViewNotifications(newNotifications, state, view);
    });
}

function addViewNotifications (
  newNotifications: WorkbookNotification[],
  state: WorkbookState,
  view: ViewDto) {
  const newNotificationsById: Record<WorkbookNotificationId, WorkbookNotification> = {};
  for (const notification of newNotifications) {
    newNotificationsById[notification.id] = notification;
  }

  if (!state.notificationsByView[view.id]) {
    state.notificationsByView[view.id] = {};
  }

  updateNotifications(state.notificationsByView[view.id], newNotificationsById);
}

function updateNotifications (
  currentNotificationsById: Record<WorkbookNotificationId, WorkbookNotification>,
  newNotificationsById: Record<WorkbookNotificationId, WorkbookNotification>) {
  // remove old notifications (only the ones added from this source)
  Object.values(currentNotificationsById)
    .forEach(notification => {
      if (notification.source === 'WORKBOOK_UPDATE') {
        delete currentNotificationsById[notification.id];
      }
    });

  // add the new ones
  Object.assign(currentNotificationsById, newNotificationsById);
}

function validateDataViews (
  view: ViewDto,
  dataViewsById: Record<DataViewId, DataViewDto>,
  activeAttributesById: Record<ActiveAttributeId, WorkbookAttributeDto>,
  state: WorkbookState): WorkbookNotification[] {
  return view.dataViews
    .flatMap((dataViewId: DataViewId) =>
      validateDataView(view, dataViewsById[dataViewId], activeAttributesById, state));
}

function validateDataView (
  view: ViewDto,
  dataView: DataViewDto,
  activeAttributesById: Record<ActiveAttributeId, WorkbookAttributeDto>,
  state: WorkbookState): WorkbookNotification[] {
  return [
    ...collectAttributeErrors(view.id, dataView, activeAttributesById),
    ...collectClassificationAttributeErrors(view.id, dataView, activeAttributesById, state),
    ...checkMissingAttributes(view, dataView),
    ...checkUnknownVisualization(view, dataView),
    ...validateColorPaletteAndGradient(view.id, dataView)
  ];
}

function collectAttributeErrors (viewId: ViewId, dataView: DataViewDto, activeAttributesById: Record<ActiveAttributeId, WorkbookAttributeDto>) {
  return Object.values(dataView.attributes).flat()
    .map(activeAttributeId => activeAttributesById[activeAttributeId])
    .flatMap(attribute => attribute.status === 'OK'
      ? []
      : [ createInvalidAttributeNotification(viewId, dataView.id, attribute.status) ]
    );
}

/**
 * Look for attribute bundle that don't meet the minimum attribute count requirements.
 */
function checkMissingAttributes (view: ViewDto, dataView: DataViewDto): WorkbookNotification[] {
  const hasMissingAttribute = Object.values(dataView.attributeBundleDefinitions)
    .some(bundleDefinition => !areRequiredAttributesFulfilled(
      view, dataView, bundleDefinition.attributeBundleName
    ));

  if (hasMissingAttribute) {
    return [ createNotification(
      'WARNING',
      'WORKBOOK_UPDATE',
      'MISSING_ATTRIBUTE',
      view.id,
      {
        dataViewId: dataView.id
      }) ];
  } else {
    return [];
  }
}

function createInvalidAttributeNotification (
  viewId: ViewId,
  dataViewId: DataViewId,
  attributeStatus: Exclude<AttributeStatus, 'OK'>
): DataViewNotification {
  const notificationType: DataViewNotificationType = `INVALID_ATTRIBUTE.${attributeStatus}`;

  return createNotification(
    'ERROR',
    'WORKBOOK_UPDATE',
    notificationType,
    viewId,
    {
      title: AttributeStatus.i18n(attributeStatus),
      detail: attributeStatusMessage(attributeStatus),
      dataViewId
    }) as DataViewNotification;
}

/**
 * Check if the minimum number of attributes is satisfied for the given bundle in the given data view.
 * @param viewDto
 * @param dataView
 * @param bundleName
 */
export function areRequiredAttributesFulfilled (viewDto: ViewDto, dataView: DataViewDto, bundleName: AttributeBundleName) {
  const minNumOfAttributes = dataView.attributeBundleDefinitions[bundleName]?.minNumberOfAttributes ?? 0;
  const numSelectedAttributes = dataView.attributes[bundleName]?.length ?? 0;
  const allAttributesSet = numSelectedAttributes >= minNumOfAttributes;
  if (!allAttributesSet && viewDto.type === 'MAP') {
    // in case we have a referenced dataview layer with an incompatible objecttype there is no
    // "fulfilled" state the user can configure by himself here, and we don't show the notification.
    const layerId = dataView.settings['DATA_VIEW_ID'] as LayerId;
    if (layerId && isDataViewReferenceByViewDto(viewDto, layerId)) {
      const notifications = getNotificationsForLayer(store.getState(), viewDto.id, layerId);
      return !notifications.some(n => n.type === 'LAYER_REFERENCE_INCOMPATIBLE_OBJECTTYPE');
    }
  }
  return allAttributesSet;
}

/**
 * Look for the "unknown visualization" status on data views.
 * @param view
 * @param dataView
 */
function checkUnknownVisualization (view: ViewDto, dataView: DataViewDto): WorkbookNotification[] {
  // Suppress the message on 'UNKNOWN_VISUALISATION' for views of type 'EXTENSION'
  // This is necessary because dynamic visualisations can become unavailable the
  // problem should only be advertised on the view cover, not in the notifications.
  if (view.type !== 'EXTENSION' && dataView.status === 'UNKNOWN_VISUALIZATION') {
    return [ createNotification(
      'ERROR',
      'WORKBOOK_UPDATE',
      'UNKNOWN_VISUALIZATION',
      view.id,
      {
        dataViewId: dataView.id
      }) ];
  }

  return [];
}

// Mapping of visualization key with corresponding bundles that contain colorPalettes used for visualization.
const NAMES_OF_BUNDLES_CONTAINING_COLOR_PALETTES_FOR_VISUALIZATION = {
  [DataViewVisualizationKey.CHART.HUNDRED_PERCENT_COLUMN_CHART]: [ CHART_BUNDLE_NAMES.DIMENSION ],
  [DataViewVisualizationKey.CHART.GROUPED_BAR_CHART]: [ CHART_BUNDLE_NAMES.DIMENSION ],
  [DataViewVisualizationKey.CHART.GROUPED_COLUMN_CHART]: [ CHART_BUNDLE_NAMES.DIMENSION ],
  [DataViewVisualizationKey.CHART.STACKED_COLUMN_CHART]: [ CHART_BUNDLE_NAMES.DIMENSION ],
  [DataViewVisualizationKey.CHART.MULTI_LINE_CHART]: [ CHART_BUNDLE_NAMES.DIMENSION ],
  [DataViewVisualizationKey.CHART.STACKED_BAR_CHART]: [ CHART_BUNDLE_NAMES.DIMENSION ],
  [DataViewVisualizationKey.CHART.HUNDRED_PERCENT_BAR_CHART]: [ CHART_BUNDLE_NAMES.DIMENSION ],
  [DataViewVisualizationKey.CHART.MIRROR_BAR_CHART]: [ CHART_BUNDLE_NAMES.DIMENSION ],
  [DataViewVisualizationKey.CHART.PIE_CHART]: [ CHART_BUNDLE_NAMES.DIMENSION ],
  [DataViewVisualizationKey.CHART.MULTI_LINE_CHART]: [ CHART_BUNDLE_NAMES.DIMENSION ],
  [DataViewVisualizationKey.CHART.STACKED_AREA_CHART]: [ CHART_BUNDLE_NAMES.DIMENSION ],
  [DataViewVisualizationKey.MAP.ROUTES]: [ MAP_BUNDLE_NAMES.COLOR_CLASSIFICATION ],
  [DataViewVisualizationKey.MAP.POLYGON]: [ MAP_BUNDLE_NAMES.COLOR_CLASSIFICATION ],
  [DataViewVisualizationKey.MAP.POINT]: [ MAP_BUNDLE_NAMES.COLOR_CLASSIFICATION ],
  [DataViewVisualizationKey.MAP.LINE]: [ MAP_BUNDLE_NAMES.COLOR_CLASSIFICATION ],
  [DataViewVisualizationKey.MAP.CLUSTERING_POINT]: [ MAP_BUNDLE_NAMES.COLOR_CLASSIFICATION ],
  [DataViewVisualizationKey.MAP.MAP_PIE_CHART]: [ MAP_BUNDLE_NAMES.PIE_CHART_DIMENSION ]
};

// Mapping of visualization key with corresponding bundles that contain gradients used for visualization.
const NAMES_OF_BUNDLES_CONTAINING_GRADIENTS_FOR_VISUALIZATION = {
  [DataViewVisualizationKey.MAP.POLYGON]: [ MAP_BUNDLE_NAMES.COLOR_CLASSIFICATION ],
  [DataViewVisualizationKey.MAP.POINT]: [ MAP_BUNDLE_NAMES.COLOR_CLASSIFICATION ],
  [DataViewVisualizationKey.MAP.LINE]: [ MAP_BUNDLE_NAMES.COLOR_CLASSIFICATION ],
  [DataViewVisualizationKey.MAP.CLUSTERING_POINT]: [ MAP_BUNDLE_NAMES.COLOR_CLASSIFICATION ],
  [DataViewVisualizationKey.MAP.MAP_PIE_CHART]: [ MAP_BUNDLE_NAMES.PIE_CHART_MEASURE ]
};

/**
 * Look for invalid color palettes and color gradients in attribute bundles that use them.
 */
function validateColorPaletteAndGradient (
  viewId: ViewId,
  dataView: DataViewDto
): WorkbookNotification[] {
  const notifications: WorkbookNotification[] = [];

  if ((NAMES_OF_BUNDLES_CONTAINING_COLOR_PALETTES_FOR_VISUALIZATION[dataView.visualizationKey] || [])
    .some(bundleName => isColorPaletteInvalidForLastAttributeInTheBundle(dataView, bundleName))) {
    notifications.push(
      createNotification(
        'WARNING',
        'WORKBOOK_UPDATE',
        'INVALID_COLOR_PALETTE',
        viewId,
        {
          dataViewId: dataView.id
        }));
  }

  if ((NAMES_OF_BUNDLES_CONTAINING_GRADIENTS_FOR_VISUALIZATION[dataView.visualizationKey] || [])
    .some(bundleName => isGradientInvalidForLastAttributeInTheBundle(dataView, bundleName))) {
    notifications.push(createNotification(
      'WARNING',
      'WORKBOOK_UPDATE',
      'INVALID_COLOR_GRADIENT',
      viewId,
      {
        dataViewId: dataView.id
      }));
  }

  return notifications;
}

/**
 * Check whether the last attribute of the bundle contains invalid colorPaletteId.
 *
 * @param dataView
 * @param bundleName - Bundle name that contains attribute that has colorPalette set
 * @return is invalid color palette on the last attribute of the bundle
 */
function isColorPaletteInvalidForLastAttributeInTheBundle (
  dataView: DataViewDto,
  bundleName: AttributeBundleName
) {
  const attributes = dataView.attributes[bundleName];
  if (attributes.length === 0) {
    return false;
  }
  const lastAttribute = attributes[attributes.length - 1];
  const { colorPaletteId } = lastAttribute;
  return !!colorPaletteId && !isValidColorPaletteId(store, colorPaletteId);
}

/**
 * Check whether the last attribute of the bundle contains invalid gradientId.
 *
 * @param dataView
 * @param bundleName - Bundle name that contains attribute that has gradient set
 * @return is invalid color palette on the last attribute of the bundle
 */
function isGradientInvalidForLastAttributeInTheBundle (
  dataView: DataViewDto,
  bundleName: AttributeBundleName
) {
  const attributes = dataView.attributes[bundleName];
  if (attributes.length === 0) {
    return false;
  }
  const lastAttribute = attributes[attributes.length - 1];
  const { colorGradientId } = lastAttribute;
  return !!colorGradientId && !isValidGradientId(store, colorGradientId);
}

function validateLayers (state: WorkbookState, view: ViewDto): WorkbookNotification[] {
  const mapSrs = getMapViewSrs(state, view.id);
  return Object.values(view.layerDefinitions)
    .flatMap(layerDefinition => ([
      ...getNotificationsForView(state, view.id),
      ...checkLayerSrs(state, view.id, layerDefinition.id, mapSrs)
    ]));
}

/**
 * Check if the given layer's SRS is compatible with the SRS assigned to the view that contains it.
 */
function checkLayerSrs (state: WorkbookState, viewId: ViewId, layerId: LayerId, mapSrs: string | undefined): WorkbookNotification[] {
  const dataView = getDataViewById(state, toDataViewId(layerId));

  if (dataView?.status !== 'VALID') {
    // only perform check for valid data views
    return [];
  }

  const layerSrsDiffersFromMap = dataView.attributes[MAP_BUNDLE_NAMES.GEOMETRY].some(attributeId => {
    const attribute = getAttributeById(state, attributeId);
    return attribute.relatedGeometryType === 'RASTER' && attribute.srs && attribute.srs !== mapSrs;
  });

  return layerSrsDiffersFromMap
    ? [ createNotification(
      'WARNING',
      'WORKBOOK_UPDATE',
      'LAYER_SRS_DIFFERS_FROM_MAP',
      viewId,
      {
        layerId }) ]
    : [];
}

function validateMapCouplingGroupLayerCount (notifications: WorkbookNotification[], state: WorkbookState, view: ViewDto) {
  if (view.type === 'MAP' && view.mapViewContext?.mapCouplingGroupId) {
    const group = getMapCouplingGroupWithStateById(view.mapViewContext?.mapCouplingGroupId, state);
    if (isCouplingLayerLimitExceededForGroupByViewId(view.id, state)) {
      notifications.push(couplingLayerExceededNotification(view.id));
      group.initiator = undefined;
      group.isLayerLimitExceeded = true;
    } else if (group.isLayerLimitExceeded) {
      synchronizeToLeftTopMostView({ groupId: group.id });
      group.isLayerLimitExceeded = false;
    }
  }
}
